import { useEffect, useMemo, useRef } from 'react';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

interface DraftAutosaveForm {
  readonly value$: Observable<any>;
  readonly touched: boolean;
}

/**
 * Autosaves a form's value while the user fills it in (debounced), and flushes synchronously
 * on unmount / `beforeunload` so last-second edits — including programmatic patches like an
 * address autocomplete filling city/state/zip — are never dropped.
 *
 * Saving only starts once the user has actually interacted: a field was blurred (`form.touched`)
 * or a real DOM input/change event fired (catches "typed into a field and closed the tab
 * without ever leaving it"). Call `stop()` after a successful server save so a pending
 * autosave can't resurrect a removed draft.
 */
export function useDraftAutosave(form: DraftAutosaveForm, enabled: boolean, save: (value: Record<string, any>) => void) {
  const stoppedRef = useRef(false);
  const interactedRef = useRef(false);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!enabled) return;

    const hasInteracted = () => form.touched || interactedRef.current;

    let latestValue: Record<string, any> | null = null;

    const flush = () => {
      if (stoppedRef.current || !hasInteracted() || !latestValue) return;
      saveRef.current(latestValue);
    };

    const markInteracted = () => (interactedRef.current = true);
    document.addEventListener('input', markInteracted, true);
    document.addEventListener('change', markInteracted, true);

    const valueSub = form.value$.subscribe(value => (latestValue = value));
    const saveSub = form.value$.pipe(debounceTime(800)).subscribe(value => {
      if (!stoppedRef.current && hasInteracted()) saveRef.current(value);
    });

    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('input', markInteracted, true);
      document.removeEventListener('change', markInteracted, true);
      valueSub.unsubscribe();
      saveSub.unsubscribe();
      flush();
    };
  }, [form, enabled]);

  return useMemo(
    () => ({
      stop: () => {
        stoppedRef.current = true;
      },
    }),
    [],
  );
}
